import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './EHLAIHomePageEnhanced.css';
import {
  ChartBarIcon,
  UserIcon,
  CreditCardIcon,
  SparklesIcon,
  AcademicCapIcon,
  BuildingStorefrontIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  Bars3Icon,
  UserCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  SunIcon,
  MoonIcon,
  MapPinIcon,
  PhoneIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';

// Memoized Service Node Component
const ServiceNode = memo(({ service, position, index, onClick, isActive }) => {
  const Icon = service.icon;
  const [isHovered, setIsHovered] = useState(false);
  
  // Staggered animation delays for ripple effects
  const animationDelay = index * 200; // milliseconds
  
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(service);
    }
  }, [service, onClick]);

  return (
    <div
      className="absolute z-10 transform -translate-x-1/2 -translate-y-1/2 animate-nodeAppear"
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        animationDelay: `${index * 0.1 + 0.5}s`
      }}
    >
      <div className="relative">
        {/* Ripple effect rings */}
        <div 
          className="absolute inset-0 rounded-full"
          style={{
            background: `linear-gradient(to right, ${service.color.includes('purple') ? '#9333ea' : service.color.includes('blue') ? '#2563eb' : service.color.includes('green') ? '#16a34a' : service.color.includes('red') ? '#dc2626' : service.color.includes('yellow') ? '#ca8a04' : service.color.includes('indigo') ? '#4f46e5' : '#6b7280'}, ${service.color.includes('purple') ? '#7c3aed' : service.color.includes('blue') ? '#3b82f6' : service.color.includes('green') ? '#22c55e' : service.color.includes('red') ? '#ef4444' : service.color.includes('yellow') ? '#eab308' : service.color.includes('indigo') ? '#6366f1' : '#9ca3af'})`,
            animation: `expandingRing 4s infinite`,
            animationDelay: `${animationDelay}ms`,
            opacity: 0,
            width: '100%',
            height: '100%'
          }}
        />
        <div 
          className="absolute inset-0 rounded-full"
          style={{
            background: `linear-gradient(to right, ${service.color.includes('purple') ? '#9333ea' : service.color.includes('blue') ? '#2563eb' : service.color.includes('green') ? '#16a34a' : service.color.includes('red') ? '#dc2626' : service.color.includes('yellow') ? '#ca8a04' : service.color.includes('indigo') ? '#4f46e5' : '#6b7280'}, ${service.color.includes('purple') ? '#7c3aed' : service.color.includes('blue') ? '#3b82f6' : service.color.includes('green') ? '#22c55e' : service.color.includes('red') ? '#ef4444' : service.color.includes('yellow') ? '#eab308' : service.color.includes('indigo') ? '#6366f1' : '#9ca3af'})`,
            animation: `expandingRing 4s infinite`,
            animationDelay: `${animationDelay + 2000}ms`,
            opacity: 0,
            width: '100%',
            height: '100%'
          }}
        />
        
        <div
          onClick={() => onClick(service)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onKeyDown={handleKeyPress}
          role="button"
          tabIndex={0}
          aria-label={`${service.name}: ${service.description}. ${service.tagline}`}
          aria-pressed={isActive}
          className={`
            relative w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full 
            ${service.color} shadow-xl flex flex-col items-center justify-center 
            text-white cursor-pointer transition-all duration-300 
            hover:scale-110 focus:scale-110 focus:outline-none focus:ring-4 
            focus:ring-white focus:ring-opacity-50
            ${isHovered ? 'transform rotate-3' : ''}
          `}
          style={{
            backfaceVisibility: 'hidden',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            transform: 'translateZ(0)',
            willChange: 'transform'
          }}
        >
        <Icon className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" style={{ transform: 'translateZ(0)' }} />
        <div className="text-xs sm:text-sm md:text-base font-bold text-center px-3 mt-1" style={{
          transform: 'translateZ(0)',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale'
        }}>
          {service.name}
        </div>
        {isHovered && (
          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-white rounded-full animate-pulse" />
        )}
      </div>
      </div>
    </div>
  );
});

ServiceNode.displayName = 'ServiceNode';

// Navigation Header Component
const NavigationHeader = memo(({ onMenuClick, isScrolled, isDarkTheme, onThemeToggle }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className={`
      fixed top-0 left-0 right-0 z-50 transition-all duration-300
      ${isScrolled ? (isDarkTheme ? 'bg-gray-900 shadow-lg' : 'bg-white shadow-lg') : 'bg-transparent'}
    `}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <div className="px-2 h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                EHLAI
              </div>
            </Link>
          </div>

          {/* Theme Toggle & Menu Buttons */}
          <div className="flex items-center gap-2">
            {/* Theme Toggle Button */}
            <button
              onClick={onThemeToggle}
              className={`p-2 rounded-lg transition-all duration-300 ${
                isDarkTheme
                  ? 'bg-yellow-400 hover:bg-yellow-500 text-gray-900'
                  : 'bg-gray-800 hover:bg-gray-700 text-yellow-400'
              }`}
              aria-label="Toggle theme"
            >
              {isDarkTheme ? (
                <SunIcon className="w-5 h-5" />
              ) : (
                <MoonIcon className="w-5 h-5" />
              )}
            </button>

            {/* Mobile Menu Button - DISABLED */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="hidden md:hidden p-2 rounded-lg hover:bg-gray-100 hover:bg-opacity-10 transition"
              aria-label="Toggle menu"
              disabled
            >
              <Bars3Icon className={`w-6 h-6 ${isScrolled ? (isDarkTheme ? 'text-white' : 'text-gray-700') : 'text-white'}`} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-white shadow-xl rounded-b-2xl p-4 animate-slideDown">
            <div className="flex flex-col space-y-2">
              <button
                onClick={() => navigate('/login')}
                className="w-full px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/register')}
                className="w-full px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 transition"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
});

NavigationHeader.displayName = 'NavigationHeader';

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center p-4">
    <ArrowPathIcon className="w-8 h-8 text-indigo-600 animate-spin" />
  </div>
);

// Error Message Component
const ErrorMessage = ({ message, onRetry }) => (
  <div className="flex items-center justify-center p-4 bg-red-50 rounded-lg">
    <ExclamationTriangleIcon className="w-6 h-6 text-red-500 mr-2" />
    <span className="text-red-700">{message}</span>
    {onRetry && (
      <button
        onClick={onRetry}
        className="ml-4 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition"
      >
        Retry
      </button>
    )}
  </div>
);

// Toast Notification Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-indigo-500';
  const Icon = type === 'success' ? CheckIcon : type === 'error' ? ExclamationTriangleIcon : null;

  return (
    <div className={`fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 ${bgColor} text-white px-4 py-3 rounded-lg shadow-xl flex items-center animate-slideUp z-50`}>
      {Icon && <Icon className="w-5 h-5 mr-2" />}
      <span className="flex-1">{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-80">
        <XMarkIcon className="w-5 h-5" />
      </button>
    </div>
  );
};

const EHLAIHomePageEnhanced = () => {
  const navigate = useNavigate();
  const [activeNode, setActiveNode] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setMessages] = useState([
    { type: 'bot', text: 'Hi! I\'m Ella, your Smart Business Assistant. How can I help you grow your business today?' }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showLines, setShowLines] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const containerRef = useRef(null);
  const chatInputRef = useRef(null);

  // Toggle theme
  const toggleTheme = useCallback(() => {
    setIsDarkTheme(prev => !prev);
  }, []);

  // Services data
  const services = useMemo(() => [
    {
      id: 'ehltax',
      name: 'EHLTAX',
      icon: ChartBarIcon,
      color: 'bg-gradient-to-br from-emerald-500 to-green-600',
      description: 'DIY Tax Preparation',
      tagline: 'Save 15+ hours',
      fullDescription: 'Professional tax preparation software designed specifically for small businesses. Get maximum deductions with AI-powered guidance.',
      features: [
        'AI-powered deduction finder',
        'Real-time tax calculation',
        'Audit protection included',
        'Multi-state filing support'
      ],
      stats: { saved: '$3.2M+', businesses: '2,400+', time: '15hrs' }
    },
    {
      id: 'emersonlee',
      name: 'Emerson H Lee',
      icon: UserIcon,
      color: 'bg-gradient-to-br from-sky-500 to-blue-600',
      description: 'Accounting, Tax & Payroll Services',
      tagline: '30+ years of expertise',
      fullDescription: 'Welcome to the Future of Accounting\n\nAt Emerson H Lee, CPA, we blend over 30 years of trusted financial expertise with the power of AI-driven technology to deliver seamless, precise, and reliable services. Whether you\'re an individual, small business, or growing enterprise, our solutions are designed to streamline your finances and fuel your success.\n\nGet personalized CPA services from certified professionals who understand small business needs.',
      contactInfo: {
        address: '6404-C Seven Corners Pl, Falls Church, VA 22044',
        phone: '(703) 237-5404',
        fax: '(703) 237-5407',
        hours: 'Monday–Friday, 9:00 AM – 5:00 PM'
      },
      features: [
        'Tax Preparation: AI-powered tax solutions for individuals and businesses',
        'Accounting Services: Full-service accounting with smart financial insights',
        'Bookkeeping: Automated, accurate, and organized recordkeeping',
        'Payroll Services: Hassle-free payroll processing with compliance monitoring',
        'Financial Consulting: Strategic advice backed by data and industry expertise',
        'Business Services: From startup to expansion, we support every step',
        'Dedicated CPA assignment',
        'Year-round tax planning',
        'Business formation assistance'
      ],
      stats: { experience: '30+ yrs', clients: '500+', retention: '94%' }
    },
    {
      id: 'ehlpayment',
      name: 'EHLPayment',
      icon: CreditCardIcon,
      color: 'bg-gradient-to-br from-violet-500 to-purple-600',
      description: 'Merchant Services',
      tagline: 'Predictable, Simple, Transparent',
      fullDescription: 'Our Mission: Empowering minority women and small businesses to take charge of their financial futures.\n\nAccept payments with the lowest processing fees in the industry. Built by a small business for small businesses.',
      features: [
        'No Cancellation Fees - Cancel anytime without penalties or hidden charges',
        'US-Based Customer Support - Real people, real help in your time zone',
        'Next-Day Deposits - Fast access to your funds when you need them',
        'Transparent Pricing - No hidden fees, annual adjustments, or surprises',
        'Free POS System - Professional point-of-sale included at no extra cost',
        'Multi-Currency Support - Accept payments in various currencies seamlessly'
      ],
      bundle: {
        title: 'Special Bundle Package',
        description: 'Get merchant services PLUS:',
        items: [
          'DIY Accounting & Tax Preparation',
          'Catch-Up Bookkeeping Services',
          'Payroll (up to 7 employees)',
          'Tax Integration & Support'
        ],
        note: 'All for one predictable price!'
      },
      trustBadge: {
        partner: 'Elavon Partner',
        details: 'Registered ISO of Elavon, Inc. Georgia (A wholly owned subsidiary of U.S. Bancorp)'
      },
      contactInfo: {
        phone: '(703) 237-5404',
        email: 'narada@ehlpayment.com'
      },
      stats: { processed: '$12M+', merchants: '800+', savings: '40%' }
    },
    {
      id: 'nailpro',
      name: 'Nail Pro Connect',
      icon: SparklesIcon,
      color: 'bg-gradient-to-br from-pink-500 to-rose-600',
      description: 'Vietnamese Nail Community',
      tagline: 'Connect & grow',
      fullDescription: 'The largest marketplace and community platform for Vietnamese nail salon owners and technicians.',
      features: [
        'Bilingual support (EN/VN)',
        'Equipment marketplace',
        'Training certifications',
        'Community networking'
      ],
      stats: { members: '5,000+', salons: '1,200+', cities: '50+' }
    },
    {
      id: 'simpletax',
      name: 'SimpleTax.AI',
      icon: AcademicCapIcon,
      color: 'bg-gradient-to-br from-amber-500 to-yellow-600',
      description: 'Tax Education',
      tagline: '5-min daily lessons',
      fullDescription: 'Learn tax strategies that save money with bite-sized daily lessons powered by AI.',
      features: [
        'Personalized learning path',
        'Real-world examples',
        'Certificate programs',
        'Live Q&A sessions'
      ],
      stats: { students: '10,000+', courses: '50+', saved: '$500+' }
    },
    {
      id: 'ehlfood',
      name: 'EHLFood',
      icon: BuildingStorefrontIcon,
      color: 'bg-gradient-to-br from-red-500 to-orange-600',
      description: 'Restaurant Platform',
      tagline: 'Built for restaurants',
      fullDescription: 'All-in-one platform for restaurant management, from inventory to customer engagement.',
      features: [
        'Digital menu & QR ordering',
        'Inventory management',
        'Staff scheduling',
        'Customer loyalty program'
      ],
      stats: { restaurants: '300+', orders: '1M+', growth: '125%' }
    },
    {
      id: 'nexad',
      name: 'NexAd',
      icon: ChatBubbleLeftRightIcon,
      color: 'bg-gradient-to-br from-indigo-500 to-blue-600',
      description: 'Local Classifieds Platform',
      tagline: 'Buy, Sell, Connect Locally',
      fullDescription: 'AI-powered classified ads platform where users can post and browse listings for goods, services, jobs, housing, and more — all organized by city or region. Connect buyers and sellers directly without middlemen.',
      features: [
        'For Sale: furniture, electronics, cars, etc.',
        'Housing: apartments, rentals, real estate',
        'Jobs & Gigs: full-time, part-time, freelance',
        'Services: cleaning, tutoring, repair, beauty',
        'Community: events, classes, volunteers'
      ],
      stats: { listings: '100K+', users: '50K+', cities: '200+' }
    }
  ], []);

  // Memoized position calculation
  const getNodePosition = useCallback((index, total) => {
    const angle = (index * 2 * Math.PI) / total - Math.PI / 2;
    const radius = window.innerWidth < 640 ? 35 : 38; // Optimized radius for screen fit
    const x = 50 + radius * Math.cos(angle);
    const y = 50 + radius * Math.sin(angle);
    return { x, y };
  }, []);

  // Memoized node positions
  const nodePositions = useMemo(() => 
    services.map((_, index) => getNodePosition(index, services.length)),
    [services, getNodePosition]
  );

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animate connection lines
  useEffect(() => {
    const timer = setTimeout(() => setShowLines(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // Handle node click with loading state
  const handleNodeClick = useCallback(async (service) => {
    setLoading(true);
    setError(null);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 300));
      setActiveNode(service);
      setToast({ message: `Viewing ${service.name}`, type: 'success' });
    } catch (err) {
      setError('Failed to load service details');
      setToast({ message: 'Error loading service', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle chat submit
  const handleChatSubmit = useCallback((e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    setMessages(prev => [...prev, { type: 'user', text: inputMessage }]);
    setInputMessage('');
    setIsTyping(true);

    // Simulate AI response
    setTimeout(() => {
      const responses = [
        'That\'s a great question! Based on your business profile, I recommend starting with EHLTAX to maximize your deductions.',
        'Many businesses like yours save over $2,000 annually using our integrated services. Would you like to see a personalized savings estimate?',
        'I can help you get started with any of our services. Which aspect of your business needs the most attention right now?',
        'Great choice! EHLTAX can help you save an average of 15 hours on tax preparation. Should I schedule a demo for you?',
        'Our payment processing saves businesses 40% on average compared to Square. Would you like to calculate your potential savings?'
      ];
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: responses[Math.floor(Math.random() * responses.length)] 
      }]);
      setIsTyping(false);
    }, 1500);
  }, [inputMessage]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (activeNode) setActiveNode(null);
        else if (showChat) setShowChat(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeNode, showChat]);

  return (
    <div className={`h-screen overflow-hidden fixed inset-0 flex flex-col transition-colors duration-500 ${
      isDarkTheme
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
        : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'
    }`}>
      {/* Navigation Header */}
      <NavigationHeader isScrolled={isScrolled} isDarkTheme={isDarkTheme} onThemeToggle={toggleTheme} />

      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className={`absolute inset-0 animate-gradient ${
          isDarkTheme
            ? 'bg-gradient-to-br from-blue-600/5 via-indigo-600/5 to-purple-600/5'
            : 'bg-gradient-to-br from-blue-200/20 via-indigo-200/20 to-purple-200/20'
        }`} />
        <div className="absolute inset-0" style={{
          backgroundImage: isDarkTheme
            ? `radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
               radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.15) 0%, transparent 50%),
               radial-gradient(circle at 40% 20%, rgba(99, 102, 241, 0.1) 0%, transparent 50%)`
            : `radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.25) 0%, transparent 50%),
               radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.25) 0%, transparent 50%),
               radial-gradient(circle at 40% 20%, rgba(99, 102, 241, 0.2) 0%, transparent 50%)`,
        }} />
      </div>

      {/* Title Section */}
      <div className="text-center pt-20 pb-2 z-10 relative flex-shrink-0">
        <h2 className={`text-lg sm:text-xl md:text-2xl font-bold mb-1 ${
          isDarkTheme ? 'text-white' : 'text-gray-900'
        }`}>
          Explore Our Integrated Services
        </h2>
        <p className={`text-xs sm:text-sm ${
          isDarkTheme ? 'text-gray-300' : 'text-gray-600'
        }`}>
          Click any service to learn more
        </p>
      </div>

      {/* Knowledge Graph Container */}
      <div ref={containerRef} className="relative flex-1 w-full flex items-center justify-center overflow-hidden">
        
        {/* Connection Lines - Draw BEFORE nodes so they appear behind */}
        {showLines && (
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ zIndex: 1 }}
          >
            <defs>
              {services.map((_, index) => (
                <linearGradient key={`gradient-${index}`} id={`gradient-${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgb(168, 85, 247)" stopOpacity="0.9" />
                  <stop offset="50%" stopColor="rgb(59, 130, 246)" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="rgb(168, 85, 247)" stopOpacity="0.6" />
                </linearGradient>
              ))}
              <filter id="glow">
                <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            {/* Draw all blurred lines first */}
            {services.map((_, index) => {
              const position = nodePositions[index];
              return (
                <line
                  key={`blur-${index}`}
                  x1="50%"
                  y1="50%"
                  x2={`${position.x}%`}
                  y2={`${position.y}%`}
                  stroke="rgba(139, 92, 246, 0.5)"
                  strokeWidth="12"
                  opacity="1"
                  style={{
                    filter: 'blur(8px)'
                  }}
                />
              );
            })}
            {/* Draw all sharp lines on top */}
            {services.map((_, index) => {
              const position = nodePositions[index];
              return (
                <line
                  key={`sharp-${index}`}
                  x1="50%"
                  y1="50%"
                  x2={`${position.x}%`}
                  y2={`${position.y}%`}
                  stroke="rgba(168, 85, 247, 1)"
                  strokeWidth="2"
                  opacity="1"
                />
              );
            })}
          </svg>
        )}

        {/* Central Node */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20" style={{marginTop: '20px'}}>
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full blur-xl opacity-50 group-hover:opacity-75 transition-opacity animate-pulse" />
            <div className={`relative w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 rounded-full shadow-2xl flex flex-col items-center justify-center cursor-pointer hover:scale-105 transition-all duration-300 border-2 ${
              isDarkTheme
                ? 'bg-gradient-to-br from-slate-800 to-slate-900 border-purple-500/30 text-white'
                : 'bg-gradient-to-br from-white to-gray-50 border-purple-300 text-gray-900'
            }`} style={{
              backfaceVisibility: 'hidden',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              transform: 'translateZ(0)'
            }}>
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent" style={{
                transform: 'translateZ(0)',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale'
              }}>
                EHLAI
              </div>
              <div className={`text-xs sm:text-sm md:text-base text-center px-6 ${
                isDarkTheme ? 'text-gray-300' : 'text-gray-600'
              }`} style={{
                transform: 'translateZ(0)',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale'
              }}>
                Tech for Minority Small Business
              </div>
            </div>
          </div>
        </div>

        {/* Service Nodes */}
        {services.map((service, index) => (
          <ServiceNode
            key={service.id}
            service={service}
            position={nodePositions[index]}
            index={index}
            onClick={handleNodeClick}
            isActive={activeNode?.id === service.id}
          />
        ))}

        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30">
            <LoadingSpinner />
          </div>
        )}
      </div>

      {/* Service Overlay */}
      {activeNode && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn"
          onClick={(e) => e.target === e.currentTarget && setActiveNode(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="service-title"
        >
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-slideUp"
            id="service-overlay"
            tabIndex={-1}
          >
            <div className={`relative ${activeNode.color} text-white p-6 sm:p-8`}>
              <button
                onClick={() => setActiveNode(null)}
                className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-purple-600"
                aria-label="Close overlay"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
              <activeNode.icon className="w-12 h-12 sm:w-16 sm:h-16 mb-4 drop-shadow-lg" />
              <h2 id="service-title" className="text-2xl sm:text-3xl font-bold mb-2">
                {activeNode.id === 'ehlpayment' ? `${activeNode.name} Merchant Services` : activeNode.name}
              </h2>
              <p className="text-base sm:text-lg opacity-95 font-medium">{activeNode.description}</p>
              {activeNode.tagline && (
                <p className="text-sm opacity-80 mt-1">{activeNode.tagline}</p>
              )}
            </div>
            
            <div className="p-6 sm:p-8 max-h-[60vh] overflow-y-auto">
              {/* Mission Statement for EHLPayment */}
              {activeNode.id === 'ehlpayment' && activeNode.fullDescription.includes('Our Mission') && (
                <div className="mb-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-100">
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                    <strong className="text-purple-700">Our Mission:</strong> Empowering minority women and small businesses to take charge of their financial futures with the lowest processing fees in the industry and exceptional US-based customer service.
                  </p>
                </div>
              )}

              {/* Full Description with Enhanced Styling */}
              <div className="mb-6">
                {activeNode.fullDescription.split('\n\n').map((paragraph, idx) => {
                  // Skip mission statement for EHLPayment (already shown above)
                  if (activeNode.id === 'ehlpayment' && paragraph.startsWith('Our Mission')) {
                    return null;
                  }
                  // Check if this is the first paragraph (the title)
                  if (idx === 0 && paragraph.startsWith('Welcome')) {
                    return (
                      <div key={idx} className="mb-6">
                        <h3 className="text-2xl font-bold bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent mb-4">
                          {paragraph}
                        </h3>
                      </div>
                    );
                  }
                  // Regular paragraphs
                  return (
                    <p key={idx} className="text-gray-700 leading-relaxed mb-4 last:mb-0">
                      {paragraph}
                    </p>
                  );
                })}
              </div>

              {/* Contact Information Card */}
              {activeNode.contactInfo && (
                <div className="mb-6 bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-200 rounded-xl p-5 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <MapPinIcon className="w-5 h-5 mr-2 text-sky-600" />
                    Contact Information
                  </h3>
                  <div className="space-y-3">
                    {/* Address */}
                    <div className="flex items-start">
                      <MapPinIcon className="w-5 h-5 mr-3 text-sky-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Location</p>
                        <p className="text-sm text-gray-600">{activeNode.contactInfo.address}</p>
                      </div>
                    </div>

                    {/* Phone & Fax */}
                    <div className="flex items-start">
                      <PhoneIcon className="w-5 h-5 mr-3 text-sky-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Phone & Fax</p>
                        <p className="text-sm text-gray-600">
                          Phone: {activeNode.contactInfo.phone}
                          <br />
                          Fax: {activeNode.contactInfo.fax}
                        </p>
                      </div>
                    </div>

                    {/* Business Hours */}
                    <div className="flex items-start">
                      <ClockIcon className="w-5 h-5 mr-3 text-sky-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Business Hours</p>
                        <p className="text-sm text-gray-600">{activeNode.contactInfo.hours}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  {activeNode.id === 'ehlpayment' && (
                    <SparklesIcon className="w-6 h-6 mr-2 text-purple-600" />
                  )}
                  {activeNode.id === 'ehlpayment' ? 'What Makes Us Different' : 'Key Features'}
                </h3>
                <div className="space-y-3">
                  {activeNode.features.map((feature, idx) => {
                    const [title, description] = feature.includes(' - ') ? feature.split(' - ') : [feature, null];
                    return (
                      <div key={idx} className={`flex items-start animate-slideInLeft ${activeNode.id === 'ehlpayment' ? 'bg-white hover:bg-gray-50 rounded-lg p-3 transition-colors' : ''}`} style={{ animationDelay: `${idx * 0.1}s` }}>
                        <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                        {description ? (
                          <div>
                            <span className="text-gray-800 font-medium">{title}</span>
                            <p className="text-sm text-gray-600">{description}</p>
                          </div>
                        ) : (
                          <span className="text-gray-700">{feature}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Special Bundle for EHLPayment */}
              {activeNode.bundle && (
                <div className="mb-8 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-5 border-2 border-amber-200">
                  <div className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0 mt-1">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
                    </svg>
                    <div>
                      <h4 className="font-bold text-amber-900 mb-2">{activeNode.bundle.title}</h4>
                      <p className="text-sm text-amber-800 mb-3">{activeNode.bundle.description}</p>
                      <ul className="space-y-1 text-sm text-amber-900">
                        {activeNode.bundle.items.map((item, idx) => (
                          <li key={idx} className="flex items-center">
                            <span className="w-1.5 h-1.5 bg-amber-600 rounded-full mr-2"></span>
                            {item}
                          </li>
                        ))}
                      </ul>
                      <p className="text-xs text-amber-700 mt-3 italic">{activeNode.bundle.note}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4 mb-8">
                {Object.entries(activeNode.stats).map(([key, value], idx) => (
                  <div key={key} className={`text-center animate-fadeIn ${activeNode.id === 'ehlpayment' ? 'bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100' : ''}`} style={{ animationDelay: `${idx * 0.1 + 0.3}s` }}>
                    <div className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                      {value}
                    </div>
                    <div className={`text-xs sm:text-sm capitalize ${activeNode.id === 'ehlpayment' ? 'text-gray-600 mt-1' : 'text-gray-500'}`}>{key}</div>
                  </div>
                ))}
              </div>

              {/* Trust Badge for EHLPayment */}
              {activeNode.trustBadge && (
                <div className="mb-6 bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center justify-center text-xs sm:text-sm text-gray-600 text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 mr-2 text-blue-600 flex-shrink-0">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
                    </svg>
                    <span>
                      <strong>{activeNode.trustBadge.partner}</strong> | {activeNode.trustBadge.details}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  disabled={activeNode.id === 'emersonlee'}
                  onClick={() => {
                    if (activeNode.id === 'emersonlee') {
                      // Disabled - do nothing
                      return;
                    }
                    if (activeNode.id === 'nexad') {
                      setToast({ message: 'Launching NexAd...', type: 'success' });
                      // Detect if mobile device
                      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                      setTimeout(() => {
                        if (isMobile) {
                          // Mobile: Open in new window
                          window.open('https://nexad.ehl.ai/', '_blank', 'noopener,noreferrer');
                        } else {
                          // Desktop: Open in new tab
                          window.open('https://nexad.ehl.ai/', '_blank');
                        }
                      }, 500);
                    } else if (activeNode.id === 'ehlfood') {
                      setToast({ message: 'Redirecting to EHLFood...', type: 'success' });
                      // Detect if mobile device
                      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                      setTimeout(() => {
                        if (isMobile) {
                          // Mobile: Open in new window
                          window.open('https://food.ehl.ai/', '_blank', 'noopener,noreferrer');
                        } else {
                          // Desktop: Open in new tab
                          window.open('https://food.ehl.ai/', '_blank');
                        }
                      }, 500);
                    } else if (activeNode.id === 'simpletax') {
                      setToast({ message: 'Redirecting to SimpleTax.AI...', type: 'success' });
                      // Detect if mobile device
                      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                      setTimeout(() => {
                        if (isMobile) {
                          // Mobile: Open in new window
                          window.open('https://showcase.ehl.ai/', '_blank', 'noopener,noreferrer');
                        } else {
                          // Desktop: Open in new tab
                          window.open('https://showcase.ehl.ai/', '_blank');
                        }
                      }, 500);
                    } else if (activeNode.id === 'ehlpayment') {
                      setToast({ message: 'Redirecting to EHLPayment...', type: 'success' });
                      // Detect if mobile device
                      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                      setTimeout(() => {
                        if (isMobile) {
                          // Mobile: Open in new window
                          window.open('https://ehlpayment.com/', '_blank', 'noopener,noreferrer');
                        } else {
                          // Desktop: Open in new tab
                          window.open('https://ehlpayment.com/', '_blank');
                        }
                      }, 500);
                    } else if (activeNode.id === 'ehltax') {
                      setToast({ message: 'Redirecting to EHLTAX...', type: 'success' });
                      // Detect if mobile device
                      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                      setTimeout(() => {
                        if (isMobile) {
                          // Mobile: Open in new window
                          window.open('https://tax.ehl.ai/', '_blank', 'noopener,noreferrer');
                        } else {
                          // Desktop: Open in new tab
                          window.open('https://tax.ehl.ai/', '_blank');
                        }
                      }, 500);
                    } else if (activeNode.id === 'nailpro') {
                      setToast({ message: 'Redirecting to Nail Pro Connect...', type: 'success' });
                      // Detect if mobile device
                      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                      setTimeout(() => {
                        if (isMobile) {
                          // Mobile: Open in new window
                          window.open('https://nailconnect.ehl.ai/', '_blank', 'noopener,noreferrer');
                        } else {
                          // Desktop: Open in new tab
                          window.open('https://nailconnect.ehl.ai/', '_blank');
                        }
                      }, 500);
                    } else {
                      setToast({ message: 'Starting your journey...', type: 'success' });
                      setTimeout(() => navigate('/register'), 500);
                    }
                  }}
                  className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all shadow-lg flex items-center justify-center group focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                    activeNode.id === 'emersonlee'
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 transform hover:scale-105'
                  }`}
                >
                  {activeNode.id === 'ehlpayment' ? 'Get Your Rate Quote' : 'Get Started'}
                  <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => {
                    setActiveNode(null);
                    setShowChat(true);
                  }}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                    activeNode.id === 'ehlpayment'
                      ? 'border-2 border-purple-300 text-purple-700 hover:bg-purple-50 hover:border-purple-500'
                      : 'border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-indigo-500 hover:text-indigo-600'
                  }`}
                >
                  {activeNode.id === 'ehlpayment' && (
                    <SparklesIcon className="w-5 h-5 mr-2" />
                  )}
                  Ask Ella {activeNode.id === 'ehlpayment' ? 'AI' : ''}
                </button>
              </div>

              {/* Contact Info Footer for EHLPayment */}
              {activeNode.id === 'ehlpayment' && activeNode.contactInfo && (
                <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                  <p className="text-sm text-gray-600 mb-2">Questions? We're here to help!</p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-sm">
                    <a href={`tel:${activeNode.contactInfo.phone.replace(/[^0-9]/g, '')}`} className="text-purple-600 hover:text-purple-700 font-medium flex items-center">
                      <PhoneIcon className="w-4 h-4 mr-1" />
                      {activeNode.contactInfo.phone}
                    </a>
                    <span className="hidden sm:inline text-gray-400">|</span>
                    <a href={`mailto:${activeNode.contactInfo.email}`} className="text-purple-600 hover:text-purple-700 font-medium flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4 mr-1">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                      </svg>
                      {activeNode.contactInfo.email}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ella Chatbot Button */}
      <button
        onClick={() => setShowChat(true)}
        className="fixed bottom-6 right-6 w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full shadow-2xl flex items-center justify-center text-white hover:scale-110 transition-all z-40 group"
        style={{ animation: 'chatBobFloat 3s ease-in-out infinite' }}
        aria-label="Open chat with Ella"
      >
        <ChatBubbleLeftRightIcon className="w-7 h-7 sm:w-8 sm:h-8" />
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
      </button>

      {/* Ella Chat Interface */}
      {showChat && (
        <div className="fixed bottom-0 sm:bottom-24 right-0 sm:right-6 w-full sm:w-96 h-[70vh] sm:h-[600px] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl z-50 flex flex-col animate-slideUp">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-4 rounded-t-2xl sm:rounded-t-2xl flex justify-between items-center">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                <ChatBubbleLeftRightIcon className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Ella</h3>
                <p className="text-xs opacity-90 flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></span>
                  Your Smart Business Assistant
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowChat(false)}
              className="hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
              aria-label="Close chat"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {chatMessages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'} animate-fadeIn`}
              >
                <div
                  className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                    msg.type === 'user'
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-br-sm'
                      : 'bg-white text-gray-800 shadow-md rounded-bl-sm'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-sm shadow-md">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t bg-white rounded-b-2xl">
            <div className="flex gap-2 mb-3 overflow-x-auto">
              {['Tax help', 'Payment setup', 'Get started', 'Pricing'].map((quick) => (
                <button
                  key={quick}
                  onClick={() => setInputMessage(quick)}
                  className="text-xs bg-gray-100 px-3 py-1.5 rounded-full hover:bg-gray-200 transition whitespace-nowrap"
                >
                  {quick}
                </button>
              ))}
            </div>
            <form onSubmit={handleChatSubmit} className="flex gap-2">
              <input
                ref={chatInputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Ask me anything..."
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-full focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-20 transition"
                aria-label="Chat message input"
              />
              <button
                type="submit"
                className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-2.5 rounded-full hover:from-indigo-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg"
                aria-label="Send message"
              >
                <PaperAirplaneIcon className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

    </div>
  );
};

export default EHLAIHomePageEnhanced;